import { DataSourceType } from "@coeff/api";
import { COLORS, handleCopy } from "@coeff/utils";
import React, { useContext, useEffect, useState } from "react";
import styled from "styled-components";

import { Button } from "../../common/Button";
import { Card } from "../../common/Card";
import { FlexColumn, FlexRow } from "../../common/FlexLayout";
import { ConnectPlugIcon, Copy } from "../../common/Icons";
import { ImportTypeIcon } from "../../common/ImportTypeIcon";
import { Radio } from "../../common/Radio";
import { Typography } from "../../common/Typography";
import { useEventManagerContext, useMessage } from "../../globals/contexts";
import { PpressContext, PPressContextParams } from "../context";

import { AuthGraphics } from "./AuthGraphics";

const StyledLogo = styled(ImportTypeIcon)`
  display: block;
  width: 60px;
  height: 60px;
`;

type AskDetailsState = {
  isAdmin: boolean | null;
};

const StyledRadio = styled(Radio)`
  display: flex;
  height: auto;
  font-size: 14px;
  color: ${COLORS.black85};
  white-space: normal;
  background-color: ${COLORS.white};
  border-radius: 4px;
  margin: 0 0 10px 0;
  padding: 10px;
`;

const CardPanel = styled.div`
  padding: 8px 8px 8px 8px;
  background: ${COLORS.white};
  border-radius: 4px;
  margin-top: 15px;
  font-weight: 500;
  font-size: 14px;
  line-height: 18px;

  display: flex;
  flex-direction: column;
  gap: 10px;
  position: relative;
`;

const StyledFooter = styled(FlexColumn)`
  padding: 15px 18px;
  position: fixed;
  bottom: 0;
  width: 100%;
  background-color: ${COLORS.white};
  z-index: 100;
  gap: 10px;
  width: 300px;
  box-shadow: 0 -5px 5px -5px ${COLORS.black15};
`;

export enum SalesforceViews {
  ASK_DETAILS_STEP = "ASK_DETAILS_STEP",
  REQUEST_ACCESS_STEP = "REQUEST_ACCESS_STEP",
}

export const IS_ADMIN_QUESTIONS = [
  {
    title: "Yes, I am a Salesforce admin",
    description: "I have Salesforce admin access",
    value: true,
  },
  {
    title: "No, I am not a Salesforce admin",
    description: "You can still connect",
    value: false,
  },
];

const AskDetailsStep: React.FC<{
  onStepChange: (values: AskDetailsState) => void;
}> = ({ onStepChange }) => {
  const [unTouchedAdmin, setUnTouchedAdmin] = useState(true);

  const [values, setValues] = useState<AskDetailsState>({ isAdmin: null });

  return (
    <>
      <AuthGraphics
        graphics={
          <div className="graphicsLogo">
            <ConnectPlugIcon />

            <StyledLogo type={DataSourceType.Salesforce} />
          </div>
        }
        dataSourceType={DataSourceType.Salesforce}
        title="Let’s connect to Salesforce"
        description=""
      />
      <Card style={{ padding: "10px", margin: "15px 10px", background: COLORS.coeblue11 }}>
        <Typography fontWeight={700} color="textPrimary" align="center">
          Are you a Salesforce admin?
        </Typography>
        <Typography
          color="textSecondary"
          align="center"
          style={{ marginTop: 10, padding: "0 10px" }}
        >
          Salesforce now requires all third-party apps (like Coefficient) to be installed by an
          admin before others can connect.
        </Typography>

        <div style={{ margin: "12px 0" }}>
          {IS_ADMIN_QUESTIONS.map(option => (
            <StyledRadio
              key={option.title}
              value={option.value}
              checked={values.isAdmin === option.value}
              onChange={event => {
                setValues(values => ({ ...values, isAdmin: event.target.value }));
                setUnTouchedAdmin(false);
              }}
            >
              <Typography fontWeight={700} fontSize={14} lineHeight={"17px"}>
                {option.title}
              </Typography>

              <Typography
                color="textTertiary"
                fontSize={14}
                lineHeight={"16px"}
                style={{ marginTop: 6, color: COLORS.black50 }}
              >
                {option.description}
              </Typography>
            </StyledRadio>
          ))}
        </div>

        <FlexRow style={{ marginTop: "20px", padding: "0 30px" }}>
          <Button
            type="primary"
            disabled={unTouchedAdmin}
            onClick={() => {
              onStepChange(values);
            }}
            style={{ width: "100%" }}
          >
            Next
          </Button>
        </FlexRow>
      </Card>
    </>
  );
};

const RequestAccessStep: React.FC<{ onProceed: () => void }> = ({ onProceed }) => {
  const { track } = useEventManagerContext();
  const message = useMessage();

  const onCopy = () => {
    const textToCopy = `Hey, I’m using Coefficient to easily work with our company data in Sheets and need your help connecting to Salesforce. Salesforce now requires third-party apps (like Coefficient) to be installed by an admin before non-admins can connect.

Here is the step-by-step help guide and video:

https://coefficient.io/salesforce-reporting/install-coefficient-on-salesforce`;

    handleCopy(textToCopy);
    message.success("Copied");
  };

  return (
    <FlexColumn>
      <FlexRow alignItems="center" justifyContent="center" style={{ marginTop: 16 }}>
        <StyledLogo type={DataSourceType.Salesforce} />
      </FlexRow>

      <Typography
        fontWeight={700}
        color="textPrimary"
        align="center"
        fontSize={18}
        lineHeight={"24px"}
        style={{ marginTop: 16 }}
      >
        Admin Help Required
      </Typography>

      <Typography
        fontWeight={500}
        fontSize={14}
        color="textSecondary"
        align="center"
        style={{ marginTop: 16, padding: "0 15px" }}
      >
        Salesforce now requires all third-party apps (like Coefficient) to be installed by an admin
        before others can connect.
      </Typography>

      <Card style={{ padding: "15px 10px", margin: "15px", background: COLORS.coeblue11 }}>
        <Typography
          fontWeight={700}
          color="textPrimary"
          align="center"
          fontSize={14}
          lineHeight={"18px"}
          style={{ padding: "0 15px" }}
        >
          Copy message and share with your admin
        </Typography>
        <CardPanel>
          <Typography
            fontWeight={500}
            color="textSecondary"
            fontSize={12}
            style={{
              marginRight: 10,
              textAlign: "left",
            }}
          >
            Hey, I’m using Coefficient to easily work with our company data in Sheets and need your
            help connecting to Salesforce. Salesforce now requires third-party apps (like
            Coefficient) to be installed by an admin before non-admins can connect.
            <br />
            <br />
            Here is the step-by-step help guide and video:
            <br />
            <br />
            https://coefficient.io/salesforce-reporting/install-coefficient-on-salesforce
          </Typography>

          <FlexRow justifyContent="end">
            <Button
              type="primary"
              onClick={() => {
                onCopy();
                track("sf_admin_help_instructions_copied", {});
              }}
              title="Copy"
            >
              <Copy /> Copy
            </Button>
          </FlexRow>
        </CardPanel>
      </Card>

      <div style={{ flex: "1 1 auto", marginBottom: 90 }}></div>

      <StyledFooter>
        <Typography fontWeight={600} fontSize={12} color="textSecondary" align="center">
          Proceed once your admin has installed Coefficient in Salesforce{" "}
        </Typography>

        <Button
          style={{ width: "100%", backgroundColor: COLORS.coeblue11 }}
          type="link"
          onClick={() => {
            onProceed();
            track("sf_admin_help_continue_clicked", {});
          }}
        >
          Proceed to Connect
        </Button>
      </StyledFooter>
    </FlexColumn>
  );
};

export const SalesforceAppSetup = ({ step }: { step?: SalesforceViews }) => {
  const [currentStep, setCurrentStep] = useState<SalesforceViews>(
    step || SalesforceViews.ASK_DETAILS_STEP
  );
  const { track } = useEventManagerContext();
  const { updateFormFromServer } = useContext(PpressContext) as PPressContextParams;

  useEffect(() => {
    track("sf_connected_app_setup_viewed", {});
  }, []);

  const showCurrentStep = (step?: SalesforceViews) => {
    switch (step) {
      case SalesforceViews.ASK_DETAILS_STEP:
        return (
          <AskDetailsStep
            onStepChange={(values: AskDetailsState) => {
              sessionStorage.setItem("has_back", "true");
              if (values.isAdmin) {
                track("sf_connected_app_setup_next_clicked", { is_salesforce_admin: true });
                updateFormFromServer({
                  is_salesforce_admin: true,
                });
              } else {
                track("sf_connected_app_setup_next_clicked", { is_salesforce_admin: false });
                updateFormFromServer({
                  show_salesforce_app_setup: true,
                  show_salesforce_warning: false,
                });
              }
            }}
          />
        );

      case SalesforceViews.REQUEST_ACCESS_STEP:
        return (
          <RequestAccessStep
            onProceed={() => {
              sessionStorage.setItem("has_back", "true");
              updateFormFromServer({
                is_salesforce_installed: true,
                show_salesforce_warning: false,
              });
            }}
          />
        );

      default:
        return null;
    }
  };

  return <>{showCurrentStep(currentStep)}</>;
};
